import Moment from 'moment';
import Datetime from 'react-datetime';
import FormLabel from '@material-ui/core/FormLabel';
import Button from 'components/CustomButtons/Button';
import GridItem from 'components/Grid/GridItem';
import CardBody from 'components/Card/CardBody';
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import FormSelectSingle from 'components/FormSelect/FormSelectSingle';
import FormTextInputNoGrid from 'components/FormTextInput/FormTextInputNoGrid';
import Close from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import SelectInput from 'components/FormSelect/SelectInput';
import { Tooltip } from '@material-ui/core';
import { Info } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react';
import produce from 'immer';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import CardWrapper from 'components/Card/CardWrapper';
import makeStyles from '@material-ui/styles/makeStyles';
import LabeledRadio from 'components/LabeledRadio/LabeledRadio';
import classNames from 'classnames';
import { blackColor, grayColor } from 'assets/jss/material-dashboard-pro-react';
import IconDeleteForeverFill from 'assets/Icons/IconDeleteForeverFill';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import TreeView from './components/TreeView';
import PROVIDERS from '../constants/providers';
import LockedByGoodspeedZoneTooltip from '../LockedByGoodspeedZoneTooltip';

const useStyles = makeStyles({
  labelHorizontal: extendedFormsStyle.labelHorizontal,
  postcodesShow: {
    marginBottom: '15px',
  },
  typeInputRoot: {
    marginBottom: '14px',
  },
  typeInput: {
    '&:before': {
      borderBottomStyle: 'none!important',
    },
  },
  inputsRow: {
    height: '190px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  deliveryHoursTitle: {
    marginTop: '20px',
  },
  deliveryHoursTooltip: {
    marginLeft: '10px',
  },
  deliveryHoursTimeLabel: {
    marginRight: '10px',
  },
  deliveryHoursSection: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  deliveryHoursContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: '0 -4px',
  },
  deliveryHoursBlock: {
    display: 'flex',
    alignItems: 'baseline',
    padding: '0 4px',
  },
  deliveryHoursAddButton: {
    color: blackColor,
    fontWeight: 'bold',
    fontSize: '16px',
    padding: 'unset',
  },
  clearDateButton: {
    position: 'absolute',
    right: '0',
    top: '6px',
    width: 'unset',
    padding: 'unset',
    color: grayColor[4],
    '&:hover': {
      color: grayColor[6],
    },
  },
  deliveryHourInputWrapper: {
    position: 'relative',
  },
  removeDatesButton: {
    width: 'unset',
    '& svg': {
      width: '20px',
      height: '20px',
    },
  },
  defaultRadio: {
    marginLeft: '10px',
    marginRight: '36px',
  },
  addressCardParentTitle: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '15px',
  },
  postcodesInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  disabledDateTimeInput: {
    opacity: '0.4',
  },
});

const Settings = ({
  state,
  setState,
  handleChange,
  drivers,
  zoneCategories,
  deliveryTypes,
  togglePostCodeModal,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { autoSyncEnabled } = useSelector(({ Brands }) => Brands.brand);

  const handleDefaultDeliveryHourChange = newDefaultIndex => {
    const newDeliveryHours = state.deliveryHours.map((deliveryHour, index) => {
      if (index === newDefaultIndex) {
        return { ...deliveryHour, default: true };
      }
      if (deliveryHour?.default === true) {
        return { ...deliveryHour, default: false };
      }
      return deliveryHour;
    });

    setState({ deliveryHours: newDeliveryHours });
  };

  const handleDeliveryHourToRemove = index => {
    const { deliveryHours } = state;

    deliveryHours.splice(index, 1);

    setState({ deliveryHours });
  };

  const handleDeliveryHourClear = (key, index) => {
    const { deliveryHours } = state;

    deliveryHours[index][key] = null;

    setState({ deliveryHours });
  };

  const handleDeliveryHourChange = (key, newHour, index) => {
    const { deliveryHours } = state;

    deliveryHours[index][key] = newHour ? new Date(newHour) : null;

    setState({ deliveryHours });
  };

  const handleChangeLocation = produceCallback => {
    const newState = produce(state.postCodes, produceCallback);

    setState(prevState => ({ ...prevState, postCodes: newState }));
  };

  const handleZoneCostChange = (key, value) => {
    setState(prevState => ({
      ...prevState,
      zoneCosts: [
        { ...(prevState.zoneCosts[0] ?? {}), [key]: value },
        ...prevState.zoneCosts.slice(1),
      ],
    }));
  };

  const getProviderLabel = provider => {
    switch (provider) {
      case PROVIDERS.CUSTOM:
        return t('zones.types.custom');
      case PROVIDERS.GOODSPEED:
        return t('zones.types.goodspeed');
      default:
        return provider;
    }
  };

  const isLockedByGoodspeedZone =
    state.provider === PROVIDERS.GOODSPEED && autoSyncEnabled;

  return (
    <>
      <CardWrapper title={t('zones.basicInfo')}>
        <CardBody>
          <GridContainer>
            <GridItem sm={4} className={classes.inputsRow}>
              <CustomInput
                labelText={t('zones.zoneType')}
                dataCy="new-zone_type"
                formControlProps={{
                  fullWidth: true,
                  className: classes.typeInputRoot,
                }}
                inputProps={{
                  name: 'provider',
                  value: getProviderLabel(state.provider),
                }}
                overrideClasses={{ disabled: classes.typeInput }}
                disabled={true}
              />
              <CustomInput
                labelText={`${t('zones.priority')} *`}
                dataCy="new-zone_priority"
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  name: 'priority',
                  type: 'number',
                  value: state.priority,
                  onChange: ev => handleChange(ev),
                }}
              />
              <CustomInput
                labelText={t('zoneCosts.price')}
                dataCy="new-zone_price"
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  name: 'price',
                  value: state.zoneCosts[0]?.price ?? '',
                  onChange: e => handleZoneCostChange('price', e.target.value),
                  type: 'number',
                  style: { marginTop: '9px' },
                }}
              />
            </GridItem>

            <GridItem sm={4} className={classes.inputsRow}>
              <LockedByGoodspeedZoneTooltip isActive={isLockedByGoodspeedZone}>
                <CustomInput
                  labelText={`${t('zones.zoneName')} *`}
                  dataCy="new-zone_name"
                  formControlProps={{ fullWidth: true }}
                  disabled={isLockedByGoodspeedZone}
                  inputProps={{
                    name: 'name',
                    value: state.name,
                    onChange: ev => handleChange(ev),
                  }}
                />
              </LockedByGoodspeedZoneTooltip>
              <LockedByGoodspeedZoneTooltip isActive={isLockedByGoodspeedZone}>
                <SelectInput
                  dataCy="new-zone_assigned_drivers"
                  // classes={classes}
                  multiple={true}
                  label={`${t('zones.assignedDrivers')} *`}
                  mapBy="name"
                  trackBy="@id"
                  name="drivers"
                  value={state.drivers}
                  options={drivers || []}
                  handleChange={handleChange}
                  size={12}
                  disabled={isLockedByGoodspeedZone}
                />
              </LockedByGoodspeedZoneTooltip>
              <CustomInput
                labelText={t('zoneCosts.vat')}
                dataCy="new-zone_vat"
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  name: 'vat',
                  value: state.zoneCosts[0]?.vat ?? '',
                  onChange: e => handleZoneCostChange('vat', e.target.value),
                  type: 'number',
                  style: { marginTop: '9px' },
                }}
              />
            </GridItem>

            <GridItem sm={4} className={classes.inputsRow}>
              <LockedByGoodspeedZoneTooltip isActive={isLockedByGoodspeedZone}>
                <CustomInput
                  labelText={`${t('zones.shortName')} *`}
                  dataCy="new-zone_short-name"
                  formControlProps={{ fullWidth: true }}
                  disabled={isLockedByGoodspeedZone}
                  inputProps={{
                    name: 'shortName',
                    value: state.shortName,
                    onChange: ev => handleChange(ev),
                  }}
                />
              </LockedByGoodspeedZoneTooltip>
              <SelectInput
                dataCy="new-zone_category"
                classes={classes}
                multiple={false}
                label={t('zones.category')}
                mapBy="name"
                trackBy="@id"
                name="category"
                value={state.category}
                options={zoneCategories || []}
                handleChange={handleChange}
                size={12}
              />
              <CustomInput
                labelText={`${t('zoneCosts.cost')}`}
                dataCy="new-zone_cost"
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  name: 'cost',
                  value: state.zoneCosts[0]?.cost ?? '',
                  onChange: e => handleZoneCostChange('cost', e.target.value),
                  type: 'number',
                  style: { marginTop: '9px' },
                }}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>

      <CardWrapper title={t('zones.deliveryTitle')}>
        <CardBody>
          <GridContainer>
            <GridItem sm={5} lg={4} xl={3}>
              <LockedByGoodspeedZoneTooltip isActive={isLockedByGoodspeedZone}>
                <FormSelectSingle
                  dataCy="new-zone_type"
                  classes={classes}
                  label={`${t('zones.type')}*`}
                  options={deliveryTypes}
                  value={state.selectedDeliveryType}
                  mapBy="value"
                  trackBy="@id"
                  name="selectedDeliveryType"
                  handleChange={ev => handleChange(ev)}
                  id="selectedDeliveryType"
                  disabled={isLockedByGoodspeedZone}
                />
              </LockedByGoodspeedZoneTooltip>
              <div className={classes.deliveryHoursSection}>
                <FormLabel
                  className={classNames(
                    classes.labelHorizontal,
                    classes.deliveryHoursTitle
                  )}
                >
                  {t('zones.deliveryHours')}
                  <div className={classes.deliveryHoursTooltip}>
                    <Tooltip
                      title={
                        <div>
                          <h5
                            dangerouslySetInnerHTML={{
                              __html: t('zones.deliveryHoursTooltip', {
                                interpolation: { escapeValue: false },
                              }),
                            }}
                          />
                        </div>
                      }
                      placement="right"
                    >
                      <Info fontSize="small" />
                    </Tooltip>
                  </div>
                </FormLabel>
                <FormLabel
                  className={classNames(
                    classes.labelHorizontal,
                    classes.deliveryHoursTitle
                  )}
                >
                  {t('zones.hours.default')}
                </FormLabel>
              </div>
              {state.deliveryHours.map((hour, index) => (
                <Fragment key={hour.id}>
                  <div className={classes.deliveryHoursContainer}>
                    <div className={classes.deliveryHoursBlock}>
                      <span className={classes.deliveryHoursTimeLabel}>
                        {t('comments.from')}
                      </span>
                      <div className={classes.deliveryHourInputWrapper}>
                        <LockedByGoodspeedZoneTooltip
                          isActive={isLockedByGoodspeedZone}
                        >
                          <Datetime
                            className={
                              isLockedByGoodspeedZone
                                ? classes.disabledDateTimeInput
                                : undefined
                            }
                            dateFormat={false}
                            value={
                              state.deliveryHours[index].hourFrom
                                ? // eslint-disable-next-line new-cap -- FIXME
                                  Moment(
                                    state.deliveryHours[index].hourFrom
                                  ).format('HH:mm')
                                : ''
                            }
                            onChange={ev =>
                              handleDeliveryHourChange('hourFrom', ev, index)
                            }
                            inputProps={{
                              placeholder: t('zones.select'),
                              readOnly: true,
                              dataCy: 'new-zone_date-from',
                              disabled: isLockedByGoodspeedZone,
                            }}
                            locale="pl"
                          />
                        </LockedByGoodspeedZoneTooltip>

                        {!isLockedByGoodspeedZone && (
                          <Button
                            data-cy="new-zone_clear_date-from"
                            className={classes.clearDateButton}
                            justIcon={true}
                            simple={true}
                            color="secondary"
                            onClick={() =>
                              handleDeliveryHourClear('hourFrom', index)
                            }
                          >
                            <Close />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className={classes.deliveryHoursBlock}>
                      <span className={classes.deliveryHoursTimeLabel}>
                        {t('comments.to')}
                      </span>

                      <div className={classes.deliveryHourInputWrapper}>
                        <LockedByGoodspeedZoneTooltip
                          isActive={isLockedByGoodspeedZone}
                        >
                          <Datetime
                            className={
                              isLockedByGoodspeedZone
                                ? classes.disabledDateTimeInput
                                : undefined
                            }
                            dateFormat={false}
                            value={
                              state.deliveryHours[index].hourTo
                                ? // eslint-disable-next-line new-cap -- FIXME
                                  Moment(
                                    state.deliveryHours[index].hourTo
                                  ).format('HH:mm')
                                : ''
                            }
                            onChange={ev =>
                              handleDeliveryHourChange('hourTo', ev, index)
                            }
                            inputProps={{
                              placeholder: t('zones.select'),
                              readOnly: true,
                              dataCy: 'new-zone_date-to',
                              disabled: isLockedByGoodspeedZone,
                            }}
                            locale="pl"
                          />
                        </LockedByGoodspeedZoneTooltip>
                        {!isLockedByGoodspeedZone && (
                          <Button
                            data-cy="new-zone_clear_date-to"
                            className={classes.clearDateButton}
                            justIcon={true}
                            simple={true}
                            color="secondary"
                            onClick={() =>
                              handleDeliveryHourClear('hourTo', index)
                            }
                          >
                            <Close />
                          </Button>
                        )}
                      </div>
                    </div>

                    {!isLockedByGoodspeedZone && (
                      <Button
                        data-cy="new-zone_date-remove"
                        justIcon={true}
                        simple={true}
                        onClick={() => handleDeliveryHourToRemove(index)}
                        color="danger"
                        className={classes.removeDatesButton}
                      >
                        <IconDeleteForeverFill />
                      </Button>
                    )}
                    <LockedByGoodspeedZoneTooltip
                      isActive={isLockedByGoodspeedZone}
                    >
                      <LabeledRadio
                        FormControlLabelProps={{
                          classes: { root: classes.defaultRadio },
                        }}
                        checked={hour?.default}
                        onChange={() => handleDefaultDeliveryHourChange(index)}
                        disabled={isLockedByGoodspeedZone}
                      />
                    </LockedByGoodspeedZoneTooltip>
                  </div>
                </Fragment>
              ))}
              {!isLockedByGoodspeedZone && (
                <Button
                  type="button"
                  className={classes.deliveryHoursAddButton}
                  simple={true}
                  color="dark"
                  onClick={() => {
                    setState({
                      deliveryHours: [
                        ...state.deliveryHours,
                        {
                          id: uuidv4(),
                          hourFrom: null,
                          hourTo: null,
                          default:
                            !state.deliveryHours ||
                            state.deliveryHours?.length === 0,
                        },
                      ],
                    });
                  }}
                >
                  {t('zones.addDeliveryHour')}
                  <AddIcon />
                </Button>
              )}
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>

      <CardWrapper
        title={t('zones.addressesTitle')}
        classesOverride={{ parentTitle: classes.addressCardParentTitle }}
        additionalContentAfterTitle={
          isLockedByGoodspeedZone ? undefined : (
            <Button
              data-cy="new-zone_add-address"
              color="success"
              onClick={togglePostCodeModal}
            >
              {t('zones.button.addAddress')}
            </Button>
          )
        }
      >
        <CardBody>
          <GridContainer>
            <GridItem sm={6}>
              <h4 className={classes.postcodesShow}>
                {t('zones.postcodesShow')}
              </h4>
              <div className={classes.postcodesInputWrapper}>
                <FormLabel className={classes.labelHorizontal}>
                  {t('zones.selectPostcode')}
                </FormLabel>
                <CustomInput
                  dataCy="new-zone_post-code"
                  customInput={FormTextInputNoGrid}
                  inputProps={{
                    value: state.postCode,
                    onChange: ev => setState({ postCode: ev.target.value }),
                  }}
                  name="postCode"
                />
                <Button
                  data-cy="new-zone_show-on-map"
                  href={`https://mapa.targeo.pl/${state.postCode}/kod-pocztowy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="info"
                  disabled={!state.postCode}
                >
                  {t('zones.showOnMap')}
                </Button>
              </div>

              <TreeView
                t={t}
                postCodes={state.postCodes}
                handleChangeLocation={handleChangeLocation}
                isEditDisabled={isLockedByGoodspeedZone}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
    </>
  );
};

export default Settings;
